import { SearchPredicate } from '@nx-smartmonkey/shared/domain';
import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useQuery } from '@tanstack/react-query';

import { toDayEnd, toDayStart } from '@nx-smartmonkey/shared/helpers';
import { planService } from '../../../../application/PlanService';
import { PlanFlat } from '../../../../domain/plan/PlanFlat';
import useErrorMiddleware from '../../../../hooks/errorMiddleware/useErrorMiddleware';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import { useShowSnackbarMessage } from '../../../../hooks/snackbarMessage/useShowSnackbarMessage';

interface UseSearchDayRangePlansProps {
  from?: string;
  to?: string;
}

type UseSearchDayRangePlansResponse = ServiceResponse<Function, PlanFlat[]> & {
  isInitialLoading: boolean;
};

export const useSearchDayRangePlans = ({ from, to }: UseSearchDayRangePlansProps): UseSearchDayRangePlansResponse => {
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { data: project } = useRetrieveActiveProject();

  const fromSearch = from ? toDayStart(new Date(from)).toUTCString() : undefined;
  const toSearch = to ? toDayEnd(new Date(to)).toUTCString() : undefined;

  const { data, isLoading, isInitialLoading, refetch, isError, isSuccess } = useQuery(
    [`planner_search_plans_range`, fromSearch, toSearch],
    (): Promise<PlanFlat[]> => {
      return planService.search({
        project: project!,
        limit: 500, // Added arbitrarily to avoid pagination for specific client with a lot of plans. It takes some time to load, but its okay.
        predicates: [
          SearchPredicate.create({
            field: `execution_date`,
            operator: `gte`,
            type: `date`,
            value: fromSearch!,
          }),
          SearchPredicate.create({
            field: `execution_date`,
            operator: `lte`,
            type: `date`,
            value: toSearch!,
          }),
        ],
      });
    },
    {
      retry: false,
      enabled: !!project && !!from && !!to,
      staleTime: Infinity,
      onError: (err: any) => {
        console.info(`Planner Get Day Plans Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }

        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    isLoading,
    isInitialLoading,
    success: isSuccess,
    run: refetch,
    data,
  };
};
